/**
 * First-visit section nudge (EPIC #1321, part 8/9; REQ D4-ONB-010).
 *
 * Contextual guidance beats up-front tours: the FIRST time a user opens a
 * section that has a registered mini-tour they have not yet engaged, a small,
 * non-blocking toast offers that section's tour -- "First time in Listings? Take
 * a 30-second tour" with Take it / No thanks. Either response permanently
 * silences that section's nudge via TourProgress (in_progress/dismissed/
 * completed are all "engaged"), so it never re-nags on another device.
 *
 * The layer is GENERIC over the central registry (sectionNudges.ts): a section
 * gets a nudge purely by registering a tour whose first step lives on that
 * route, so the agent set (4/9) and the admin set (#1326) get nudges for free
 * with no per-page wiring. Mounted ONCE inside TourProvider (next to
 * WelcomePrompt) so it covers every persona's surfaces.
 *
 * Guard rails (acceptance criteria):
 *  - exactly once per untoured section, never again after either response;
 *  - never stacks (one nudge at a time, dismissed before the next route);
 *  - never interrupts an active tour (suppressed while ``isRunning``);
 *  - never on the welcome-prompt landing (the welcome prompt owns that moment);
 *  - never offers a tour all of whose steps are gated out for this user
 *    (mirrors the engine's ``isTourAvailable``), so back-office/CUSTOM presets
 *    are not nagged toward a surface they cannot open;
 *  - never covers the help-assistant panel: while it is open (e.g. after the
 *    assistant opened a page on a desktop) a pending offer is held back and
 *    shown once the panel closes (#3607).
 *
 * Styling: a compact toast in house brand tokens, bottom-sheet-safe on mobile
 * (anchored bottom, full-width on mobile / floating card on >= sm). Tailwind +
 * CSS-variable tokens only; Lucide icons; agency-neutral copy via {agency}; no
 * emojis. Non-blocking (no overlay / focus trap) -- it must never gate the page.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Compass, X } from "lucide-react";
import { useAuth } from "../../contexts/auth";
import { useTour } from "../../contexts/tour";
import { useIsViewingAs } from "../../contexts/viewAs";
import { useSupportChat } from "../../contexts/supportChat";
import { useModules } from "../../contexts/modules";
import { useBranding } from "../../hooks/useBranding";
import { landingPathForUser } from "../auth/ProtectedRoute";
import {
  sectionTourForRoute,
  sectionNudgeLabel,
} from "../../lib/tours/sectionNudges";
import { isTourAvailable } from "../../lib/tours/filter";
import type { TourGateContext } from "../../lib/tours/types";
import { setSectionNudgeVisible } from "../../lib/tours/nudgePresence";
import {
  fetchTourProgress,
  upsertTourProgress,
  type TourStatus,
} from "../../api/tours";

/**
 * A section nudge is silenced once the user has engaged its tour in ANY way --
 * started it, dismissed it, or completed it. Only a tour the user has never
 * touched (no row, or an explicit "not_started" reset) is still nudgeable.
 */
function isSilenced(status: TourStatus | undefined): boolean {
  return status !== undefined && status !== "not_started";
}

/**
 * While viewing as an agent (D2-SEC-011, #3962) there is no nudge at all: the
 * server refuses the tour-progress read and both answers are refused writes, so
 * the inner watcher (and its fetch) is never even mounted.
 */
export function SectionNudge() {
  const viewingAs = useIsViewingAs();
  return viewingAs ? null : <SectionNudgeWatcher />;
}

function SectionNudgeWatcher() {
  const { user, loading: authLoading } = useAuth();
  const { isRunning, startTour } = useTour();
  const { isOpen: supportOpen } = useSupportChat();
  const { isEnabled, loading: modulesLoading } = useModules();
  const branding = useBranding();
  const location = useLocation();

  // Live permission/module gate -- the SAME shape the engine + sidebar use, so a
  // tour all of whose steps are filtered out is never nudged (D4-ONB-004).
  const gate: TourGateContext = useMemo(
    () => ({
      has: (flag) => Boolean(user?.permission_flags?.[flag]),
      isModuleEnabled: (module) => isEnabled(module),
      // Whole-tour audience gate inputs (issue #1347): never nudge a plain agent
      // toward an admin / back-office desk tour they cannot open.
      isAdmin: user?.role === "admin",
      preset: user?.permission_preset ?? null,
    }),
    [user, isEnabled],
  );

  // Server-side silenced state, loaded once per user. ``null`` = not yet loaded;
  // a Map of tour_id -> status once fetched. We optimistically silence ids the
  // user answers this session so a nudge never re-fires before the round-trip.
  const [silenced, setSilenced] = useState<Map<string, TourStatus> | null>(null);
  const loadedForUser = useRef<number | null>(null);

  // The tour currently being offered (null = nothing showing). At most one.
  const [offer, setOffer] = useState<{ tourId: string; label: string } | null>(
    null,
  );
  // Routes already considered this mount, so a single page load nudges at most
  // once even across re-renders / query-string changes on the same path.
  const consideredRef = useRef<Set<string>>(new Set());

  // (Re)load the caller's tour progress when the user changes. Fire-and-forget;
  // a failed fetch just means no nudges this session (fail safe -- never nags).
  useEffect(() => {
    const uid = user?.id ?? null;
    if (uid === null) {
      setSilenced(null);
      loadedForUser.current = null;
      consideredRef.current = new Set();
      setOffer(null);
      return;
    }
    if (loadedForUser.current === uid) return;
    loadedForUser.current = uid;
    setSilenced(null);
    consideredRef.current = new Set();
    setOffer(null);
    let cancelled = false;
    void fetchTourProgress()
      .then((rows) => {
        if (cancelled) return;
        setSilenced(new Map(rows.map((r) => [r.tour_id, r.status])));
      })
      .catch(() => {
        if (cancelled) return;
        setSilenced(new Map()); // empty = nothing known silenced yet
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Decide whether to show a nudge on the current route.
  useEffect(() => {
    const path = location.pathname;

    // A pending offer is for the section the user was ON. If they navigate away
    // before answering it, drop it -- a Listings nudge must never linger on the
    // Deals page (and the new route gets its own fresh evaluation below).
    if (offer && sectionTourForRoute(path)?.id !== offer.tourId) {
      setOffer(null);
      return; // re-runs cleanly with offer === null
    }

    // A tour that started (via Take it) suppresses nudges everywhere it runs.
    if (isRunning) {
      if (offer) setOffer(null);
      return;
    }

    // Wait until auth + modules + progress have resolved so gating + silencing
    // are computed against final state (a premature decision could mis-gate).
    if (authLoading || modulesLoading) return;
    if (!user || silenced === null) return;
    // Never stack a second nudge while one is already showing.
    if (offer) return;

    // One nudge per page load: once a path has been considered, do not re-offer
    // it (covers query-string changes + re-renders on the same section page).
    if (consideredRef.current.has(path)) return;

    // Never nudge on the user's own welcome-prompt landing -- the welcome prompt
    // owns that first moment (one greeting at a time).
    const landing = landingPathForUser(user.role, user.permission_preset);
    if (path === landing) return;

    const tour = sectionTourForRoute(path);
    if (!tour) return;

    // Mark considered up-front: even if we decline below (silenced / gated), we
    // never re-evaluate this path on this mount.
    consideredRef.current.add(path);

    if (isSilenced(silenced.get(tour.id))) return; // already engaged -> silent
    if (!isTourAvailable(tour, gate)) return; // all steps gated out for this user

    setOffer({
      tourId: tour.id,
      label: sectionNudgeLabel(tour),
    });
  }, [
    authLoading,
    modulesLoading,
    user,
    silenced,
    isRunning,
    offer,
    location.pathname,
    gate,
  ]);

  /** Optimistically silence + persist a terminal answer for the offered tour. */
  const silence = useCallback((tourId: string, status: TourStatus) => {
    setSilenced((prev) => {
      const next = new Map(prev ?? []);
      next.set(tourId, status);
      return next;
    });
    void upsertTourProgress({ tour_id: tourId, status }).catch(() => {
      /* fire-and-forget: a failed write may re-nag next session, acceptable */
    });
  }, []);

  const takeIt = useCallback(() => {
    if (!offer) return;
    const { tourId } = offer;
    setOffer(null);
    // startTour records the tour as "in_progress" immediately (recordTourStarted),
    // which silences the nudge server-side even if the user later cancels.
    startTour(tourId);
  }, [offer, startTour]);

  const noThanks = useCallback(() => {
    if (!offer) return;
    silence(offer.tourId, "dismissed");
    setOffer(null);
  }, [offer, silence]);

  // The help bubble shares this corner and steps aside while a nudge shows (#3604).
  const showing = offer !== null && !supportOpen;
  useEffect(() => {
    setSectionNudgeVisible(showing);
    return () => setSectionNudgeVisible(false);
  }, [showing]);

  // The help-assistant panel owns the corner while it is open; hold the offer.
  if (!offer || supportOpen) return null;

  // "First time in Listings? Take a quick tour." -- the label is the section
  // tour's title with " tour" stripped, falling back to the agency name.
  const sectionName = offer.label || branding.agency_name;

  return (
    <div
      // Bottom-anchored, non-blocking. Full-width sheet on mobile, floating card
      // on >= sm. High z so it sits above page chrome but BELOW the tour overlay
      // (z 10000) so it can never cover an active tour.
      className="fixed inset-x-0 bottom-0 z-[9000] flex justify-center px-3 pb-3 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:px-0 sm:pb-0 pointer-events-none"
      role="region"
      aria-label="Guided tour suggestion"
    >
      <div className="pointer-events-auto w-full sm:w-auto sm:max-w-sm rounded-t-xl sm:rounded-lg bg-[var(--color-bg-card)] border border-[var(--color-border)] border-l-[3px] border-l-brand-accent shadow-lg">
        <div className="flex items-start gap-3 p-4">
          <Compass className="h-5 w-5 mt-0.5 shrink-0 text-brand-accent" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-[var(--color-text-primary)]">
              First time in {sectionName}?
            </p>
            <p className="mt-0.5 text-sm text-[var(--color-text-secondary)]">
              Take a quick 30-second tour of this section.
            </p>
            <div className="mt-3 flex flex-col-reverse gap-2 sm:flex-row">
              <button
                type="button"
                onClick={noThanks}
                className="min-h-[44px] sm:min-h-0 px-3 py-2 rounded-lg border border-[var(--color-border)] text-sm font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] transition-colors"
              >
                No thanks
              </button>
              <button
                type="button"
                onClick={takeIt}
                className="min-h-[44px] sm:min-h-0 px-3 py-2 rounded-lg bg-brand-accent text-white text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Take it
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={noThanks}
            aria-label="Dismiss tour suggestion"
            className="shrink-0 -mr-1 -mt-1 p-2.5 sm:p-1.5 rounded-lg text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
